import _ from 'underscore'

export const RGBA_REGEXP = /^rgba\s?\((?:\s?,?(?<red>\d{1,3})\s?,?)(?:\s?,?(?<green>\d{1,3})\s?,?)(?:\s?,?(?<blue>\d{1,3})\s?,?)(?:\s?,?(?<alpha>[\d.]+)\s?,?)\)$/i
export const RGB_REGEXP = /^rgb\s?\((?:\s?,?(?<red>\d{1,3})\s?,?(?:\s?,?(?<green>\d{1,3})\s?,?))(?:\s?,?(?<blue>\d{1,3})\s?,??)\)$/i
export const HEX_REGEXP = /^#?(?:[0-9a-fA-F]{3}){1,2}$/i

export function colorToRGBA (color, alpha = 1) {
  if (color === undefined) return
  if (color === null) return
  if (RGBA_REGEXP.test(color)) {
    const groups = color.match(RGBA_REGEXP).groups
    if (!groups) return
    return `rgba(${groups.red},${groups.green},${groups.blue},${alpha})`
  } else if (RGB_REGEXP.test(color)) {
    return rgbToRgba(color, alpha)
  } else if (HEX_REGEXP.test(color)) {
    const hex = intToHex(color)
    if (!hex) return
    const rgb = hexToRgb(hex)
    if (!rgb) return
    return `rgba(${rgb.red},${rgb.green},${rgb.blue},${alpha})`
  } else {
    const hex = intToHex(color)
    if (!hex) return
    const rgb = hexToRgb(hex)
    if (!rgb) return
    return `rgba(${rgb.red},${rgb.green},${rgb.blue},${alpha})`
  }
}

export function rgbToRgba (color, alpha = 1) {
  if (!RGB_REGEXP.test(color)) { return }
  if (alpha === undefined || alpha === null) { alpha = 1 }
  const groups = color.match(RGB_REGEXP).groups
  return `rgba(${groups.red},${groups.green},${groups.blue},${alpha})`
}

export function intToHex (int) {
  if (int === null || int === undefined) { return null }

  if (HEX_REGEXP.test(int)) {
    int = int.toString(16)
    if (int.charAt(0) !== '#') {
      int = `000000${int}`.slice(-6)
      return `#${int}`
    }
    return int
  }

  if (Number.isNaN(Number(int))) { return null }

  int = `000000${int.toString(16)}`
  return `#${int.slice(-6)}`
}

/**
 * Returns an object of 3 numbers representing the red, green, and blue values
 * of the given hex. If the hex number can't be parse, undefined is returned.
 * Got some help from http://stackoverflow.com/a/6444043/703108
 */
export function hexToRgb (hex) {
  if ((typeof hex) !== 'string') { return null }

  // strip the leading # if it's there
  hex = hex.replace(/^#/, '')

  if (hex.length === 3) {
    hex = hex.replace(/(.)/g, '$1$1')
  }

  if (hex.length === 6) {
    return {
      red: parseInt(hex.substr(0, 2), 16),
      green: parseInt(hex.substr(2, 2), 16),
      blue: parseInt(hex.substr(4, 2), 16)
    }
  } else {
    return null
  }
}

/*
 * @namespace Util
 * @mixin
 */
const Color = {
  RGBA_REGEXP,
  RGB_REGEXP,
  HEX_REGEXP,
  colorToRGBA,
  rgbToRgba,
  intToHex,
  hexToRgb,
  // Return the fgColor that has the highest contrast with the background color
  bestContrast (bgColor, ...fgColors) {
    const best = _(fgColors).max(fgColor => Color.contrast(bgColor, fgColor))

    // Compensate for Underscore.js stupidity.
    if (best === -Infinity) {
      return undefined
    } else {
      return best
    }
  },

  /*
   * http://juicystudio.com/article/luminositycontrastratioalgorithm.php
   * (L1+.05) / (L2+.05)
   * L1 is the higher value (of text or background) and L2 is the lower value.
   * the 0.05 offset is included to compensate for contrast ratios that occur when a value is at or near zero, and for ambient light effects.
   * Accepts a hex string or an object with `red`, `green`, and `blue` properties.
   */
  contrast (c1, c2) {
    const l1 = Color.luminosity(c1)
    const l2 = Color.luminosity(c2)
    if ((l1 == null) || (l2 == null)) { return }
    if (l1 > l2) {
      return (l1 + 0.05) / (l2 + 0.05)
    } else {
      return (l2 + 0.05) / (l1 + 0.05)
    }
  },

  /*
   * http://juicystudio.com/article/luminositycontrastratioalgorithm.php
   * .2126*R + .7152*G + .0722B using linearised R, G, and B values.
   * Accepts an rgb(a) string, a hex string, or an object
   * with `red`, `green`, and `blue` properties.
   */
  luminosity (color) {
    let rgb = {}

    if (color === null || color === undefined) { color = {} }

    if ((color.red != null) && (color.green != null) && (color.blue != null)) {
      rgb = color
    } else if (RGBA_REGEXP.test(color)) {
      const groups = color.match(RGBA_REGEXP).groups
      if (groups) {
        rgb = {
          red: groups.red,
          green: groups.green,
          blue: groups.blue
        }
      }
    } else if (RGB_REGEXP.test(color)) {
      const groups = color.match(RGB_REGEXP).groups
      if (groups) {
        rgb = {
          red: groups.red,
          green: groups.green,
          blue: groups.blue
        }
      }
    } else {
      // Assume hex
      rgb = Color.hexToRgb(color)
    }

    if (rgb) {
      let { red, green, blue } = rgb
      red = Color.linearise(red)
      green = Color.linearise(green)
      blue = Color.linearise(blue)
      return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue)
    }
  },

  /*
   * http://juicystudio.com/article/luminositycontrastratioalgorithm.php
   * Linearised R (for example) = (R/FS)^2.2 where FS is full scale value (255 for 8 bit color channels).
   */
  linearise (channel, fullScale) {
    if (fullScale == null) { fullScale = 255 }
    return Math.pow(channel / fullScale, 2.2)
  }
}

export default Color
